import type { Joi } from '@ksyos/koi';
import { Koi } from '@ksyos/koi';
import { SpecialismCode } from '@common/enums';
import type { ISchemaContainer } from './schemaContainer';
import { HolterLeadCount } from '../enums/holterLeadCount';
import { Profession } from '../enums/profession';
import { replaceKoiError } from './replaceKoiError';
import { emailSchema } from './schemas/emailSchema';
import { phoneNumberNoErrorSchema, phoneNumberSchema } from './schemas/phoneNumberSchema';
import { relationNumberSchema } from './schemas/relationNumberSchema';
import { zorgMailNummerSchema } from './schemas/zorgMailNummerSchema';
import { specialismCodeValidator } from './utils/accountFieldsCustomValidationFunctions';
import { supervisorRequired } from './utils/accountFieldsRequirementsByProfession';
import { isToday } from './utils/validateIsToday';

interface IEmploymentEmailAddressValidated {
    emailAddress: string | null;
}

export interface IEmploymentContactDetailsValidated extends IEmploymentEmailAddressValidated {
    emailAddressAlt: string | null;
    emergencyNumber: string | null;
}

export interface IEmploymentPaymentDetailsValidated {
    emailAddressPayouts: string | null;
    relationNumber: string | null;
}

interface IEmploymentKsyosRolesValidated {
    managerRoleId?: string | null;
}

export interface IEmploymentGetOrganizationsValidated {
    excludeId?: string;
    isActive?: boolean;
    isGeneralPractitionerOrHidha?: boolean;
    isNoManager?: boolean;
    organizationId?: string;
    overlapEndDate?: string;
    overlapStartDate?: string;
    practitionerId: string;
}

interface IEmploymentZorgMailNummerValidated {
    zorgmailNummer: string | null;
}

export interface IEmploymentAddEditValidated extends IEmploymentAddEditNoPractitionerValidated {
    practitionerId: string;
}

export interface IEmploymentAddEditNoPractitionerValidated
    extends IEmploymentKsyosRolesValidated,
        IEmploymentZorgMailNummerValidated {
    emailAddressPayouts: string | null;
    emergencyNumber: string | null;
    endDate: string | null;
    groupManagerId: string | null;
    identifierDeclarationPractitionerId: string | null;
    organizationId: string;
    profession: Profession | null;
    relationNumber: string | null;
    specialismCode: string | null;
    startDate: string | Date;
}

interface IEmploymentIsPrivacyModeEnabledValidated {
    isPrivacyModeEnabled: boolean;
}

interface IEmploymentHolterLeadCountValidated {
    holterLeadCount: HolterLeadCount;
}

export interface IEmploymentGeneralSettingsValidated {
    endDate?: string | null;
    identifierDeclarationPractitionerId?: string | null;
    profession: Profession;
    specialismCode: string | null;
    startDate: string;
}

export interface IDeactivateEmploymentValidated {
    endDate: string | Date;
    startDate: string | Date;
}

export interface IEmploymentsToUpdateValidated extends IEmploymentKsyosRolesValidated {
    id: string;
}

export type IEmploymentUpdateValidated =
    | IEmploymentIsPrivacyModeEnabledValidated
    | IEmploymentZorgMailNummerValidated
    | IEmploymentAddEditValidated
    | IEmploymentHolterLeadCountValidated;

const employmentEmailAddress = Koi.object({
    emailAddress: emailSchema.allow(null),
});

const employmentContactDetails = employmentEmailAddress.keys({
    emailAddressAlt: emailSchema.allow(null),
    emergencyNumber: phoneNumberSchema.allow(null),
});

const employmentPaymentDetails = Koi.object().keys({
    emailAddressPayouts: emailSchema.allow(null),
    relationNumber: relationNumberSchema,
});

const deactivateEmployment = Koi.object().keys({
    endDate: Koi.koi()
        .date()
        .endDate()
        .custom(isToday)
        .message('Datum moet vandaag zijn')
        .error((errors: Joi.ErrorReport[]) => replaceKoiError(errors, 'any.custom', 'koi_isNotToday')),
    startDate: Koi.koi().date(),
});

const employmentsToUpdate = Koi.object().keys({
    id: Koi.string().uuid().required(),
    managerRoleId: Koi.string().uuid().allow(null),
});

const employmentZorgmailNummer = Koi.object().keys({
    zorgmailNummer: zorgMailNummerSchema.allow(null),
});

export const employmentZorgmailNummerSchema: ISchemaContainer<IEmploymentZorgMailNummerValidated> = {
    schema: employmentZorgmailNummer.options({ convert: false, presence: 'required' }),
};

const employmentSpecialism = Koi.object().keys({
    specialismCode: Koi.when('profession', {
        is: Profession.medicalSpecialist,
        otherwise: specialismCodeValidator,
        then: Koi.koi().enum(SpecialismCode),
    }),
});

export const employmentAddEditNoPractitioner = Koi.object()
    .keys({
        emailAddressPayouts: emailSchema.allow(null),
        emergencyNumber: phoneNumberNoErrorSchema.allow(null),
        endDate: Koi.koi().date().endDate().allow(null),
        groupManagerId: Koi.string().uuid().allow(null),
        identifierDeclarationPractitionerId: Koi.string().uuid().allow(null),
        organizationId: Koi.string().uuid(),
        profession: Koi.koi().enum(Profession).allow(null),
        relationNumber: relationNumberSchema,
        startDate: Koi.koi().date(),
    })
    .concat(employmentSpecialism)
    .concat(employmentZorgmailNummer);

export const employmentGetOrganizationsSchema: ISchemaContainer<IEmploymentGetOrganizationsValidated> = {
    schema: Koi.object({
        excludeId: Koi.string().uuid().optional(),
        isActive: Koi.boolean().optional(),
        isGeneralPractitionerOrHidha: Koi.boolean().optional(),
        isNoManager: Koi.boolean().optional(),
        organizationId: Koi.string().uuid().optional(),
        overlapEndDate: Koi.koi().date().optional(),
        overlapStartDate: Koi.koi().date().optional(),
        practitionerId: Koi.string().uuid().required(),
    }).options({ presence: 'required' }),
};

export const employmentAddEdit = employmentAddEditNoPractitioner.keys({
    practitionerId: Koi.string().uuid(),
});

const holterLeadCount = Koi.object().keys({ holterLeadCount: Koi.koi().enum(HolterLeadCount) });

export const employmentUpdateSchema: ISchemaContainer<IEmploymentUpdateValidated> = {
    schema: Koi.alternatives()
        .try(
            Koi.object().keys({ isPrivacyModeEnabled: Koi.boolean() }),
            employmentZorgmailNummer,
            holterLeadCount,
            employmentAddEdit
        )
        .options({ convert: false, presence: 'required' }),
};

export const employmentContactDetailsSchema: ISchemaContainer<IEmploymentContactDetailsValidated> = {
    schema: employmentContactDetails.options({ convert: false, presence: 'required' }),
};

export const employmentPaymentDetailsSchema: ISchemaContainer<IEmploymentPaymentDetailsValidated> = {
    schema: employmentPaymentDetails.options({ convert: false, presence: 'required' }),
};

export const employmentAddEditSchema: ISchemaContainer<IEmploymentAddEditValidated> = {
    schema: employmentAddEdit.options({ convert: false, presence: 'required' }),
};

export const employmentGeneralSettingsSchema: ISchemaContainer<IEmploymentGeneralSettingsValidated> = {
    schema: Koi.object({
        endDate: Koi.koi().date().endDate().allow(null).optional(),
        identifierDeclarationPractitionerId: Koi.when('profession', {
            is: Koi.valid(...supervisorRequired),
            otherwise: Koi.string().uuid().allow(null).optional(),
            then: Koi.string().uuid().required(),
        }),
        profession: Koi.koi().enum(Profession).required(),
        startDate: Koi.koi().date().required(),
    })
        .concat(employmentSpecialism)
        .options({ presence: 'required' }),
};

export const deactivateEmploymentSchema: ISchemaContainer<IDeactivateEmploymentValidated> = {
    schema: deactivateEmployment.options({ convert: false, presence: 'required' }),
};

export const employmentsToUpdateSchema: ISchemaContainer<IEmploymentsToUpdateValidated> = {
    schema: employmentsToUpdate.options({ convert: false, presence: 'required' }),
};

export function isEmploymentAddEdit(employment: IEmploymentUpdateValidated): employment is IEmploymentAddEditValidated {
    return (employment as IEmploymentAddEditValidated).groupManagerId !== undefined;
}
